import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@medflex/components/stories/Button';
import ModalTW from '@medflex/components/stories/Modal';
import { useTranslation } from 'react-i18next';
import { showSuccessToast, showAlertToast } from '@medflex/components/stories/Toast';
import { trackEvent } from '@medflex/utils/Tracking';
import PhysicianCreateSubContactModalModule from './PhysicianCreateSubContactModalModule';
import MultiplePatientInvitationModule from './MultiplePatientInvitationModule';
import TextSnippetsEditModal from './TextSnippetsEditModal';
import UserTagsEditModal from './UserTagsEditModal';
import PhysicianStaffMemberEditModule from './PhysicianStaffMemberEditModule';
import useLocationSearchParams from '../../hooks/useLocationSearchParams';
import useModal from '../hooks/useModal';
import MulticastModule from './MulticastModule';
import useInvitationRequests from '../hooks/useInvitationRequests';
import useStaff from '../hooks/useStaff';
import ContactInfoModal from './ContactInfoModule/ContactInfoModal';
import PhysicianResendInvitationModule from './PhysicianResendInvitationModule';
import PhysicianPersonalProfileEditModule from './PhysicianPersonalProfileEditModule';
import PhysicianClinicProfileEditModule from './PhysicianClinicProfileEditModule';
import SessionsCreateModule from './SessionsCreateModule/SessionsCreateModule';
import NewPermanentLinkModule from './NewPermanentLinkModule';
import ProfileImageModule from './ProfileImageModule';
import SessionsDetailModule from './SessionsDetailModule';
import SessionsCancelModule from './SessionsCancelModule';
import SessionsResendModule from './SessionsResendModule';
import SharePatientContactModule from './SharePatientContactModule';
import PhysicianInvitePhysicianModule from './PhysicianInvitePhysicianModule';
import PhysicianPagesLinkModule from './PhysicianPagesLinkModule';
import ContactDeleteModule from './ContactDeleteModule';
import SessionsRemoveParticipantModule from './SessionsRemoveParticipantModule';
import SessionChatDeleteMessageModule from './SessionChatModule/SessionChatDeleteMessageModule';
import SessionChatDetailModal from './SessionChatModule/SessionChatDetailModal';
import SessionChatCreateModule from './SessionChatCreateModule';
import SessionChatLeaveModal from './SessionChatModule/SessionChatLeaveModal';
import SessionChatRemoveModal from './SessionChatModule/SessionChatRemoveModal';
import SessionChatRemoveParticipantModal from './SessionChatModule/SessionChatRemoveParticipantModal';
import SessionChatDeleteMessagesModal from './SessionChatModule/SessionChatDeleteMessagesModal';
import SessionChatDeleteSettingsModule from './SessionChatModule/SessionChatDeleteSettingsModule';
import NewMessageSwitchModule from './NewMessageSwitchModule';
import SessionChatSearchModule from './SessionChatSearchModule';
import ContactSearchModule from './ContactSearchModule';
import PatientPermanentLinkModule from './PatientPermanentLinkModule';
import PatientPermanentQRCodeModule from './PatientPermanentQRCodeModule';
import TextSnippetsDeleteModule from './TextSnippetsDeleteModule';
import StatusDeleteModal from './StatusDeleteModal';
import StaffTypeModule from './StaffTypeModule';
import PhysicianScheduleCallSwitchModule from './PhysicianScheduleCallSwitchModule';
import FeatureUpgradeSessionMeeting from './FeatureUpgradeModules/FeatureUpgradeSessionMeeting';
import FeatureUpgradeSessionMeetingGroup from './FeatureUpgradeModules/FeatureUpgradeSessionMeetingGroup';
import FeatureUpgradeSnippets from './FeatureUpgradeModules/FeatureUpgradeSnippets';
import FeatureUpgradePhysicianStatus from './FeatureUpgradeModules/FeatureUpgradePhysicianStatus';
import FeatureUpgradeMulticast from './FeatureUpgradeModules/FeatureUpgradeMulticast';
import FeatureUpgradeStaffAssistant from './FeatureUpgradeModules/FeatureUpgradeStaffAssistant';
import FeatureUpgradeStaffPhysician from './FeatureUpgradeModules/FeatureUpgradeStaffPhysician';
import FeatureUpgradeChat from './FeatureUpgradeModules/FeatureUpgradeChat';
import FeatureUpgradePam from './FeatureUpgradeModules/FeatureUpgradePam';
import FeatureUpgrade from './FeatureUpgradeModules/FeatureUpgrade';
import WidgetSettingsModal from './PhysicianAdministrationModule/WidgetSettingsModal';
import ContactInviteModal from './ContactInviteModal';
import GreetingMessageEditModal from './GreetingMessageEditModal';
import StatusComposeModal from './StatusComposeModal';
import AddressEditModal from './AddressEditModal';
import OpeningHoursEditModal from './OpeningHoursEditModal';
import ServicesEditModal from './ServicesEditModal';
import { MODALS } from '../hooks/useIsModal';
import PhysicianStatusModal from '../modals/PhysicianStatusModal';
import NewQuestionnaireModal from '../modals/NewQuestionnaireModal';
import AutoResponseMessageEditModal from '../modals/AutoResponseMessageEditModal';
import UserTagsDeleteModule from './UserTagsDeleteModule';
import SmsSettingsEditModal from '../modals/SmsSettingsEditModal';

const DeleteInvitationModule = () => {
  const { close } = useModal();
  const dispatch = useDispatch();
  const { t } = useTranslation('physician');
  const { contactId } = useLocationSearchParams();
  const [name, setName] = React.useState('');
  const contacts = useSelector(state => state.chatContacts);

  const closeModal = () => close({ modal: 'delete_invitation', contactId });

  React.useEffect(() => {
    if (!contactId) {
      closeModal();
    }
  }, []);

  React.useEffect(() => {
    if (contacts) {
      const contact = contacts.find(c => c.id === contactId);
      if (contact) {
        setName(contact.user.fullNameWithTitle);
      }
    }
  }, [contactId, contacts]);

  const handleDelete = async e => {
    e.preventDefault();
    if (contactId) {
      try {
        await dispatch.patientInvitations.delete({ contactId });
        showSuccessToast(t('modals.delete_invitation.title'), null, {
          toastId: 'invitationDeleteSuccess',
        });
      } catch {
        showAlertToast(
          t('modals.delete_invitation.failure'),
          t('modals.delete_invitation.failure_text'),
          { toastId: 'deleteInvitationFailed' },
        );
      }
    }
    closeModal();
  };

  return (
    <ModalTW
      showCloseButton
      onClickCloseButton={closeModal}
      title={t('modals.delete_invitation.title')}
    >
      <div data-component="DeleteInvitationModule" className="flex flex-col">
        <p>{t('modals.delete_invitation.text_one', { allowHtml: true, name })}</p>
        <p className="mt-4">{t('modals.delete_invitation.text_two')}</p>
      </div>
      <p className="mt-16 mb-4 flex w-full justify-center lg:mt-8">
        <Button type="button" onClick={handleDelete}>
          {t('modals.delete_invitation.button')}
        </Button>
      </p>
    </ModalTW>
  );
};

const IgnoreInvitationRequestModule = () => {
  const { close } = useModal();
  const { t } = useTranslation('physician');
  const { deleteInvitationRequest, setIsSeen } = useInvitationRequests();
  const [isLoading, setIsLoading] = React.useState();
  const { id } = useLocationSearchParams();
  const handleIgnore = async event => {
    if (!isLoading) {
      setIsLoading(true);
      event.preventDefault();
      if (id) {
        try {
          await deleteInvitationRequest({ id });
          setIsSeen();
          showSuccessToast(t('modals.ignore_invitation_request.success'), null, {
            toastId: 'ignoreInvitationSuccess',
          });
          trackEvent(event);
          close({ modal: 'ignoreInvitationRequest', id });
        } catch {
          showAlertToast(
            t('modals.ignore_invitation_request.failure'),
            t('modals.ignore_invitation_request.failure_text'),
            { toastId: 'ignoreInvitationFailed' },
          );
          setIsLoading(false);
        }
      }
    }
  };

  return (
    <ModalTW
      showCloseButton
      onClickCloseButton={() => close({ modal: 'ignoreInvitationRequest', id })}
      title={t('modals.ignore_invitation_request.title')}
    >
      <div data-component="DeleteMessageModule" className="mt-12 flex flex-col lg:mt-0">
        <p>{t('modals.ignore_invitation_request.text_confirmation')}</p>
      </div>
      <p className="mt-16 mb-4 flex w-full justify-center lg:mt-8">
        <Button isLoading={isLoading} type="button" onClick={handleIgnore}>
          {t('modals.ignore_invitation_request.button')}
        </Button>
      </p>
    </ModalTW>
  );
};

const PhysicianStaffMemberDeleteModule = () => {
  const { close } = useModal();
  const { t } = useTranslation('physician');
  const { staff, deleteStaffMember } = useStaff();
  const [isLoading, setIsLoading] = React.useState();
  const { id } = useLocationSearchParams();

  const name = React.useMemo(() => {
    if (staff && id) {
      const staffMember = staff.find(m => m.id === id);
      return (
        (staffMember &&
          `${staffMember.title ? `${staffMember.title}` : ''}${staffMember.firstName} ${
            staffMember.lastName
          }`) ||
        ''
      );
    }
    return '';
  }, [staff, id]);

  const handleDelete = async event => {
    if (!isLoading) {
      setIsLoading(true);
      event.preventDefault();
      if (id) {
        try {
          await deleteStaffMember({ id });
          showSuccessToast(t('modals.delete_staff_member.success'), null, {
            toastId: 'deleteStaffSuccess',
          });
          trackEvent(event);
          close({ modal: 'delete_staff_member', id });
        } catch {
          showAlertToast(
            t('modals.delete_staff_member.failure'),
            t('modals.delete_staff_member.failure_text'),
            { toastId: 'deleteStaffFailed' },
          );
          setIsLoading(false);
        }
      }
    }
  };

  return (
    <ModalTW
      showCloseButton
      onClickCloseButton={() => close({ modal: 'delete_staff_member', id })}
      title={t('modals.delete_staff_member.title', { name, allowHtml: true })}
    >
      <div data-component="DeleteMessageModule" className="mt-12 flex flex-col lg:mt-0">
        <p>{t('modals.delete_staff_member.text_confirmation')}</p>
      </div>
      <p className="mt-16 mb-4 flex w-full justify-center lg:mt-8">
        <Button isLoading={isLoading} type="button" onClick={handleDelete}>
          {t('modals.delete_staff_member.button')}
        </Button>
      </p>
    </ModalTW>
  );
};

const PhysicianModalsModule = () => {
  const { modal } = useLocationSearchParams();
  const { open, close } = useModal();

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (open && close) {
      const handleKeys = event => {
        if (!!modal && (event.key === 'Escape' || event.key === 'Esc') /* IE */) {
          close({ modal });
        }
      };

      document.addEventListener('keydown', handleKeys);

      return () => {
        document.removeEventListener('keydown', handleKeys);
      };
    }
  }, [open, close, modal]);

  switch (modal) {
    case 'status':
      return <StatusComposeModal />;
    case 'statusDelete':
      return <StatusDeleteModal />;
    case 'multicast':
      return <MulticastModule />;
    case MODALS.pages:
      return <PhysicianPagesLinkModule />;
    case 'delete_invitation':
      return <DeleteInvitationModule />;
    case 'resend_invitation':
      return <PhysicianResendInvitationModule />;
    case 'multiPatientInvitation':
      return <MultiplePatientInvitationModule />;
    case 'sharePatientContact':
      return <SharePatientContactModule />;
    case 'createSubcontact':
      return <PhysicianCreateSubContactModalModule />;
    case 'ignoreInvitationRequest':
      return <IgnoreInvitationRequestModule />;
    case 'textSnippetsDelete':
      return <TextSnippetsDeleteModule />;
    case 'textSnippetsEdit':
      return <TextSnippetsEditModal />;
    case 'userTagsEdit':
      return <UserTagsEditModal />;
    case 'userTagsDelete':
      return <UserTagsDeleteModule />;
    case 'staff_type':
      return <StaffTypeModule />;
    case 'staff_member':
      return <PhysicianStaffMemberEditModule />;
    case 'delete_staff_member':
      return <PhysicianStaffMemberDeleteModule />;
    case 'contact_info':
      return <ContactInfoModal />;
    case 'profile_personal':
      return <PhysicianPersonalProfileEditModule />;
    case 'profile_clinic':
      return <PhysicianClinicProfileEditModule />;
    case MODALS.sessionsCreate:
      return <SessionsCreateModule />;
    case 'sessionsRemoveParticipant':
      return <SessionsRemoveParticipantModule />;
    case 'sessionsDetail':
      return <SessionsDetailModule />;
    case 'sessionsCancel':
      return <SessionsCancelModule />;
    case 'sessionsResend':
      return <SessionsResendModule />;
    case 'newPermanentLink':
      return <NewPermanentLinkModule />;
    case 'editProfileImage':
      return <ProfileImageModule />;
    case 'invitePhysician':
      return <PhysicianInvitePhysicianModule />;
    case 'deleteContact':
      return <ContactDeleteModule />;
    case 'videoauswahl':
      return <PhysicianScheduleCallSwitchModule />;
    case MODALS.deleteSessionChatMessage:
      return <SessionChatDeleteMessageModule />;
    case MODALS.sessionChatDetail:
      return <SessionChatDetailModal />;
    case 'sessionChatCreate':
      return <SessionChatCreateModule />;
    case MODALS.sessionChatLeave:
      return <SessionChatLeaveModal />;
    case 'sessionChatDeleteSettings':
      return <SessionChatDeleteSettingsModule />;
    case 'sessionChatRemove':
      return <SessionChatRemoveModal />;
    case 'sessionChatRemoveParticipant':
      return <SessionChatRemoveParticipantModal />;
    case 'sessionChatDeleteMessages':
      return <SessionChatDeleteMessagesModal />;
    case 'newMessage':
      return <NewMessageSwitchModule />;
    case 'sessionChatSearch':
      return <SessionChatSearchModule />;
    case 'contactSearch':
      return <ContactSearchModule />;
    case 'patientPermanentLink':
      return <PatientPermanentLinkModule />;
    case 'patientPermanentQRCode':
      return <PatientPermanentQRCodeModule />;
    case 'featureUpgradeSnippets':
      return <FeatureUpgradeSnippets />;
    case 'featureUpgradeSessionMeeting':
      return <FeatureUpgradeSessionMeeting />;
    case 'featureUpgradeSessionMeetingGroup':
      return <FeatureUpgradeSessionMeetingGroup />;
    case 'featureUpgradePhysicianStatus':
      return <FeatureUpgradePhysicianStatus />;
    case 'featureUpgradeMulticast':
      return <FeatureUpgradeMulticast />;
    case 'featureUpgradeStaffAssistant':
      return <FeatureUpgradeStaffAssistant />;
    case 'featureUpgradeStaffPhysician':
      return <FeatureUpgradeStaffPhysician />;
    case 'featureUpgradeChat':
      return <FeatureUpgradeChat />;
    case 'featureUpgradePam':
      return <FeatureUpgradePam />;
    case 'featureUpgrade':
      return <FeatureUpgrade />;
    case MODALS.physicianPagesWidgetSettings:
      return <WidgetSettingsModal />;
    case 'contactInvite':
      return <ContactInviteModal />;
    case 'greetingMessageEdit':
      return <GreetingMessageEditModal />;
    case MODALS.address:
      return <AddressEditModal />;
    case MODALS.openingHours:
      return <OpeningHoursEditModal />;
    case MODALS.services:
      return <ServicesEditModal />;
    case MODALS.PHYSICIAN_STATUS_MODAL:
      return <PhysicianStatusModal />;
    case MODALS.NEW_QUESTIONNAIRE:
      return <NewQuestionnaireModal />;
    case MODALS.autoResponseMessageEdit:
      return <AutoResponseMessageEditModal />;
    case MODALS.smsSettingsEdit:
      return <SmsSettingsEditModal />;
    default:
      return null;
  }
};

export default PhysicianModalsModule;
